/**
 * PositionDisclosure — Phase 86. "See what's included".
 *
 * The audit trail behind every headline number. Two lists that matter equally:
 * what was counted, and what was NOT — with the reason. The ₹2L that went
 * missing before Phase 86 disappeared because nothing was ever obliged to
 * explain an exclusion. Now every suppressed resource has to name itself.
 */
import React from 'react';
import {
  Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription,
} from '@/components/ui/dialog';
import { formatMoney } from '@/lib/format';
import FreshnessChip from './FreshnessChip';

const BUCKET_LABEL = {
  available_cash: 'Available cash',
  invested:       'Long-term investments',
  other_assets:   'Other assets',
  debt:           'Outstanding debt',
};

function fmtDate(iso) {
  if (!iso) return null;
  try {
    return new Date(iso).toLocaleDateString(undefined, { day: 'numeric', month: 'short', year: 'numeric' });
  } catch { return iso; }
}

function Row({ r, base }) {
  const native = formatMoney(Math.abs(r.native_amount || 0), r.currency);
  const showBase = r.currency !== base && r.base_amount != null;
  return (
    <li className="flex items-start justify-between gap-4 py-2.5" data-testid={`disclosure-row-${r.id}`}>
      <div className="min-w-0">
        <p className="text-sm text-charcoal truncate">{r.name}</p>
        <div className="flex flex-wrap items-center gap-x-2 gap-y-1 mt-0.5">
          <span className="text-[11px] text-muted-foreground">
            {r.reason_label || BUCKET_LABEL[r.bucket] || r.bucket}
          </span>
          {r.as_of && (
            <span className="text-[11px] text-muted-foreground/80">· {fmtDate(r.as_of)}</span>
          )}
          {r.balance_type === 'estimated' && (
            <span className="text-[11px] text-[#8A6A20]">· 2120 estimate</span>
          )}
          <FreshnessChip freshness={r.freshness} />
        </div>
      </div>
      <div className="text-right shrink-0">
        <p className={`num text-sm font-medium ${r.side === 'liability' ? 'text-[#A84E38]' : 'text-charcoal'}`}>
          {r.side === 'liability' ? '−' : ''}{native}
        </p>
        {showBase && (
          <p className="num text-[11px] text-muted-foreground/80 mt-0.5">
            ≈ {r.side === 'liability' ? '−' : ''}{formatMoney(Math.abs(r.base_amount), base)}
          </p>
        )}
      </div>
    </li>
  );
}

export function PositionDisclosure({ open, onOpenChange, position }) {
  if (!position) return null;
  const base = position.base_currency;
  const included = position.included_resources || [];
  const excluded = position.excluded_resources || [];
  const assets = included.filter((r) => r.side === 'asset');
  const debts  = included.filter((r) => r.side === 'liability');

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="max-w-2xl max-h-[85vh] overflow-y-auto" data-testid="position-disclosure">
        <DialogHeader>
          <DialogTitle className="font-display text-xl font-medium">What&rsquo;s included</DialogTitle>
          <DialogDescription>
            Every resource behind your position, in its own currency. Totals are
            converted to {base} only at the point of adding them up.
          </DialogDescription>
        </DialogHeader>

        <section className="mt-2">
          <div className="flex items-baseline justify-between border-b border-border pb-2">
            <p className="text-xs font-semibold uppercase tracking-widest text-muted-foreground">
              Counted as assets · {assets.length}
            </p>
            <p className="num text-sm font-medium text-charcoal">{formatMoney(position.assets, base)}</p>
          </div>
          <ul className="divide-y divide-border/40" data-testid="disclosure-assets">
            {assets.map((r) => <Row key={`${r.source_table}-${r.id}`} r={r} base={base} />)}
          </ul>
        </section>

        {debts.length > 0 && (
          <section className="mt-5">
            <div className="flex items-baseline justify-between border-b border-border pb-2">
              <p className="text-xs font-semibold uppercase tracking-widest text-muted-foreground">
                Counted as debt · {debts.length}
              </p>
              <p className="num text-sm font-medium text-[#A84E38]">
                −{formatMoney(position.liabilities, base)}
              </p>
            </div>
            <ul className="divide-y divide-border/40" data-testid="disclosure-debts">
              {debts.map((r) => <Row key={`${r.source_table}-${r.id}`} r={r} base={base} />)}
            </ul>
          </section>
        )}

        <section className="mt-5">
          <div className="border-b border-border pb-2">
            <p className="text-xs font-semibold uppercase tracking-widest text-muted-foreground">
              Not counted · {excluded.length}
            </p>
          </div>
          {excluded.length === 0 ? (
            <p className="text-xs text-muted-foreground py-3" data-testid="disclosure-excluded-empty">
              Nothing was left out. Every account, holding and loan you&rsquo;ve told
              2120 about is contributing to the numbers above.
            </p>
          ) : (
            <ul className="divide-y divide-border/40" data-testid="disclosure-excluded">
              {excluded.map((r) => <Row key={`${r.source_table}-${r.id}`} r={r} base={base} />)}
            </ul>
          )}
        </section>

        <p className="text-[11px] text-muted-foreground mt-5 pt-4 border-t border-border/60 leading-relaxed">
          FX as of {position.fx?.as_of_date || '—'} · source {position.fx?.source || '—'}
          {position.liability_estimates?.applied > 0 && (
            <>
              {' · '}
              {position.liability_estimates.applied} loan balance
              {position.liability_estimates.applied === 1 ? '' : 's'} shown as a 2120
              estimate because it&rsquo;s newer than the lender&rsquo;s last figure
            </>
          )}
        </p>
      </DialogContent>
    </Dialog>
  );
}

export default PositionDisclosure;
